// A saved link is navigation state, never consent to share location.
export const INVITE_TIMEOUT_MS = 15000;

export function createContactInviteFlow({
  token, api, getAuth, navigate, storage, notify, onChange,
  timeoutMs = INVITE_TIMEOUT_MS,
}) {
  let active = true;
  let state = { invite: null, error: null, busy: null };
  const requests = new Set();
  const path = `/contacts/invites/by-token/${encodeURIComponent(token)}`;

  const update = (changes) => {
    state = { ...state, ...changes };
    if (active) onChange(state);
  };
  const remember = () => {
    try { storage.setItem("pending_invite", JSON.stringify({ kind: "contact", token })); }
    catch { /* Storage restrictions must not prevent accepting or leaving. */ }
  };
  const clearPending = () => {
    try {
      const pending = JSON.parse(storage.getItem("pending_invite") || "null");
      if (pending?.kind === "contact" && pending.token === token) {
        storage.removeItem("pending_invite");
      }
    } catch { /* Best effort; an old saved link still cannot auto-accept. */ }
  };
  const dispose = () => {
    active = false;
    for (const controller of requests) controller.abort();
    requests.clear();
  };
  const leave = (destination = "/") => {
    if (!active) return;
    clearPending();
    dispose();
    navigate(destination, { replace: true });
  };
  const message = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === "string" ? detail : error?.code === "INVITE_TIMEOUT"
      ? "Request timed out. Please try again." : fallback;
  };

  // Bound waiting even if the transport fails to settle after aborting.
  const request = async (method, suffix = "") => {
    const controller = new AbortController();
    requests.add(controller);
    let timer;
    let abort;
    const interrupted = new Promise((_, reject) => {
      abort = () => reject(Object.assign(new Error("Request cancelled"), { name: "AbortError" }));
      controller.signal.addEventListener("abort", abort, { once: true });
      timer = setTimeout(() => {
        reject(Object.assign(new Error("Request timed out"), { code: "INVITE_TIMEOUT" }));
        controller.abort();
      }, timeoutMs);
    });
    try {
      const config = { signal: controller.signal, timeout: timeoutMs };
      return await Promise.race([
        method === "get" ? api.get(path, config) : api.post(path + suffix, undefined, config),
        interrupted,
      ]);
    } finally {
      clearTimeout(timer);
      controller.signal.removeEventListener("abort", abort);
      requests.delete(controller);
    }
  };

  const load = async () => {
    if (!active) return;
    update({ invite: null, error: null, busy: null });
    remember();
    try {
      const response = await request("get");
      if (!active) return;
      if (!response?.data || typeof response.data.status !== "string") {
        throw new Error("Invalid invitation response");
      }
      update({ invite: response.data });
      // Deliberately no acceptance here, including after sign-in or restart.
    } catch (error) {
      if (active) update({ error: message(error, "Invite not found or unavailable") });
    }
  };

  const accept = async () => {
    const auth = getAuth();
    if (!active || state.busy || state.invite?.status !== "pending" || auth.loading) return;
    if (!auth.user) {
      remember();
      dispose();
      navigate(`/signin?next=${encodeURIComponent(`/join/${encodeURIComponent(token)}`)}`);
      return;
    }
    update({ busy: "accept" });
    try {
      const response = await request("post", "/accept");
      if (!active) return;
      if (response?.data?.already_connected !== true && response?.data?.connected !== true) {
        throw new Error("Invalid acceptance response");
      }
      const name = response.data.owner_name || state.invite.owner_name || "your contact";
      if (response.data.already_connected) notify.message(`You're already connected with ${name}`);
      else notify.success(`Connected with ${name}`);
      leave("/dashboard");
    } catch (error) {
      if (active) notify.error(message(error, "Failed to accept. Please try again."));
    } finally {
      if (active) update({ busy: null });
    }
  };

  const decline = async () => {
    if (!active || state.busy || state.invite?.status !== "pending") return;
    update({ busy: "decline" });
    try {
      const response = await request("post", "/decline");
      if (!active) return;
      if (response?.data?.ok === true) notify.message("Invitation declined");
      else notify.error("Could not decline invitation");
    } catch (error) {
      if (active) notify.error(message(error, "Couldn't reach server — leaving invite"));
    } finally {
      if (active) leave();
    }
  };

  return { load, accept, decline, leave, dispose };
}
